import { useEffect, useRef, useState } from 'react';

/**
 * Intersection Observer scroll-reveal hook.
 * Returns [ref, isVisible].
 */
export const useReveal = (options = {}) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.unobserve(el);
                }
            },
            {
                threshold: options.threshold || 0.08,
                rootMargin: options.rootMargin || '0px 0px -40px 0px',
            }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [options.threshold, options.rootMargin]);

    return [ref, visible];
};

/**
 * Attaches mousemove tracking to a container.
 * Sets --mouse-x and --mouse-y CSS vars on .bento-card children.
 */
export const useBentoGlow = () => {
    const ref = useRef(null);

    useEffect(() => {
        const container = ref.current;
        if (!container) return;

        const handleMouseMove = (e) => {
            const cards = container.querySelectorAll('.bento-card');
            cards.forEach(card => {
                const rect = card.getBoundingClientRect();
                card.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
                card.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
            });
        };

        container.addEventListener('mousemove', handleMouseMove);
        return () => container.removeEventListener('mousemove', handleMouseMove);
    }, []);

    return ref;
};

// Legacy compat aliases
export const useScrollReveal = useReveal;
export const useMouseGlow = useBentoGlow;
